import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const DentalChart = ({ selectedTooth, onToothSelect, chartingMode, onModeChange }) => {
  const [chartData, setChartData] = useState({});
  const [activeCondition, setActiveCondition] = useState(null);

  const toothNumbers = [
    // Upper Right
    [1, 2, 3, 4, 5, 6, 7, 8],
    // Upper Left
    [9, 10, 11, 12, 13, 14, 15, 16],
    // Lower Left
    [17, 18, 19, 20, 21, 22, 23, 24],
    // Lower Right
    [25, 26, 27, 28, 29, 30, 31, 32]
  ];

  const conditions = [
    { value: 'caries', label: 'Caries', color: 'bg-red-500', icon: 'AlertCircle' },
    { value: 'filling', label: 'Filling', color: 'bg-blue-500', icon: 'Circle' },
    { value: 'crown', label: 'Crown', color: 'bg-yellow-500', icon: 'Crown' },
    { value: 'missing', label: 'Missing', color: 'bg-gray-500', icon: 'X' },
    { value: 'implant', label: 'Implant', color: 'bg-green-500', icon: 'Anchor' },
    { value: 'root-canal', label: 'Root Canal', color: 'bg-purple-500', icon: 'Activity' },
    { value: 'bridge', label: 'Bridge', color: 'bg-orange-500', icon: 'Link' },
    { value: 'mobility', label: 'Mobility', color: 'bg-pink-500', icon: 'Move' }
  ];

  const surfaces = ['M', 'O', 'D', 'B', 'L'];

  const handleToothClick = (toothNum) => {
    onToothSelect(toothNum);
  };

  const handleConditionApply = (toothNum, condition) => {
    setChartData(prev => ({
      ...prev,
      [toothNum]: {
        ...prev?.[toothNum],
        condition,
        mode: chartingMode
      }
    }));
  };

  const getToothColor = (toothNum) => {
    const tooth = chartData?.[toothNum];
    if (!tooth) return 'bg-white';
    
    const condition = conditions?.find(c => c?.value === tooth?.condition);
    return condition?.color || 'bg-white';
  };

  return (
    <div className="space-y-6">
      {/* Charting Mode Toggle */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-foreground">Charting Mode:</h3>
            <div className="flex gap-2">
              <button
                onClick={() => onModeChange('existing')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  chartingMode === 'existing' ?'bg-primary text-primary-foreground' :'bg-background text-muted-foreground hover:bg-muted'
                }`}
              >
                Existing Conditions
              </button>
              <button
                onClick={() => onModeChange('planned')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  chartingMode === 'planned' ?'bg-primary text-primary-foreground' :'bg-background text-muted-foreground hover:bg-muted'
                }`}
              >
                Planned Treatment
              </button>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" iconName="RotateCcw">
              Reset Chart
            </Button>
            <Button variant="default" size="sm" iconName="Save">
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      {/* Condition Legend */}
      <div className="bg-card rounded-xl border border-border p-4">
        <h3 className="font-semibold text-foreground mb-3">Conditions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
          {conditions?.map((condition) => (
            <button
              key={condition?.value}
              onClick={() => setActiveCondition(condition?.value)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                activeCondition === condition?.value
                  ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
              }`}
            >
              <div className={`w-4 h-4 rounded ${condition?.color}`} />
              <span className="text-xs font-medium text-foreground">{condition?.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Dental Chart - Odontogram */}
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="space-y-8">
          {/* Upper Arch */}
          <div>
            <div className="text-center mb-4">
              <h3 className="text-sm font-semibold text-muted-foreground">UPPER ARCH</h3>
            </div>
            <div className="grid grid-cols-2 gap-8">
              {/* Upper Right */}
              <div>
                <p className="text-xs text-center text-muted-foreground mb-2">Right</p>
                <div className="grid grid-cols-8 gap-2">
                  {toothNumbers?.[0]?.map((toothNum) => (
                    <button
                      key={toothNum}
                      onClick={() => {
                        handleToothClick(toothNum);
                        if (activeCondition) {
                          handleConditionApply(toothNum, activeCondition);
                        }
                      }}
                      className={`aspect-square rounded-lg border-2 transition-all ${
                        selectedTooth === toothNum
                          ? 'border-primary scale-110 shadow-lg'
                          : 'border-border hover:border-primary/50'
                      } ${getToothColor(toothNum)}`}
                    >
                      <div className="text-xs font-bold text-foreground">{toothNum}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Upper Left */}
              <div>
                <p className="text-xs text-center text-muted-foreground mb-2">Left</p>
                <div className="grid grid-cols-8 gap-2">
                  {toothNumbers?.[1]?.map((toothNum) => (
                    <button
                      key={toothNum}
                      onClick={() => {
                        handleToothClick(toothNum);
                        if (activeCondition) {
                          handleConditionApply(toothNum, activeCondition);
                        }
                      }}
                      className={`aspect-square rounded-lg border-2 transition-all ${
                        selectedTooth === toothNum
                          ? 'border-primary scale-110 shadow-lg'
                          : 'border-border hover:border-primary/50'
                      } ${getToothColor(toothNum)}`}
                    >
                      <div className="text-xs font-bold text-foreground">{toothNum}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Lower Arch */}
          <div>
            <div className="text-center mb-4">
              <h3 className="text-sm font-semibold text-muted-foreground">LOWER ARCH</h3>
            </div>
            <div className="grid grid-cols-2 gap-8">
              {/* Lower Left */}
              <div>
                <div className="grid grid-cols-8 gap-2">
                  {toothNumbers?.[2]?.map((toothNum) => (
                    <button
                      key={toothNum}
                      onClick={() => {
                        handleToothClick(toothNum);
                        if (activeCondition) {
                          handleConditionApply(toothNum, activeCondition);
                        }
                      }}
                      className={`aspect-square rounded-lg border-2 transition-all ${
                        selectedTooth === toothNum
                          ? 'border-primary scale-110 shadow-lg'
                          : 'border-border hover:border-primary/50'
                      } ${getToothColor(toothNum)}`}
                    >
                      <div className="text-xs font-bold text-foreground">{toothNum}</div>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-center text-muted-foreground mt-2">Left</p>
              </div>
              
              {/* Lower Right */}
              <div>
                <div className="grid grid-cols-8 gap-2">
                  {toothNumbers?.[3]?.map((toothNum) => (
                    <button
                      key={toothNum}
                      onClick={() => {
                        handleToothClick(toothNum);
                        if (activeCondition) {
                          handleConditionApply(toothNum, activeCondition);
                        }
                      }}
                      className={`aspect-square rounded-lg border-2 transition-all ${
                        selectedTooth === toothNum
                          ? 'border-primary scale-110 shadow-lg'
                          : 'border-border hover:border-primary/50'
                      } ${getToothColor(toothNum)}`}
                    >
                      <div className="text-xs font-bold text-foreground">{toothNum}</div>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-center text-muted-foreground mt-2">Right</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Selected Tooth Details */}
      {selectedTooth && (
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">Tooth #{selectedTooth} Details</h3>
            <Button variant="ghost" size="sm" iconName="X" onClick={() => onToothSelect(null)} />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Surfaces Affected</label>
              <div className="flex gap-2">
                {surfaces?.map((surface) => (
                  <button
                    key={surface}
                    className="flex-1 px-3 py-2 rounded-lg border border-border hover:border-primary hover:bg-primary/10 text-sm font-medium transition-all"
                  >
                    {surface}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Mobility</label>
              <Select
                options={[
                  { value: '0', label: 'Class 0 - Normal' },
                  { value: '1', label: 'Class 1 - Slight' },
                  { value: '2', label: 'Class 2 - Moderate' },
                  { value: '3', label: 'Class 3 - Severe' }
                ]}
                placeholder="Select mobility class"
              />
            </div>
          </div>
          
          <div className="mt-4">
            <label className="block text-sm font-medium text-foreground mb-2">Clinical Notes</label>
            <textarea
              placeholder="Add notes about this tooth..."
              className="w-full min-h-[80px] px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DentalChart;